<template>
  <div class="tw-bg-gray-50 tw-rounded-lg tw-p-3">
    <!-- the meter, its alive dot and its energize status; how it is read is in the vendor column next to it -->
    <div class="tw-flex tw-items-center tw-gap-2 tw-mb-1">
      <span class="tw-text-xs tw-font-semibold tw-text-gray-500 tw-uppercase tw-tracking-wide">
        Meter <span class="tw-font-mono tw-font-normal tw-normal-case tw-tracking-normal tw-text-gray-400">{{ serialNumber }}</span>
      </span>
      <span
        v-if="isLoading"
        class="tw-w-3 tw-h-3 tw-border-2 tw-border-gray-300 tw-border-t-blue-400 tw-rounded-full tw-animate-spin tw-flex-shrink-0"
      />
      <span v-else class="tw-w-2.5 tw-h-2.5 tw-rounded-full tw-flex-shrink-0" :class="aliveClass" />
      <span
        v-if="meterDetail.status"
        class="tw-ml-auto tw-text-xs tw-px-2 tw-py-0.5 tw-rounded tw-font-medium tw-uppercase tw-tracking-wider"
        :class="statusBadgeClass"
      >{{ statusLabel }}</span>
    </div>

    <div class="tw-grid tw-grid-cols-2 tw-gap-x-4 tw-gap-y-1 tw-text-sm">
      <!-- the meter's own energize address: on a CVZ each meter has its own fake number (66_CVZ1) -->
      <template v-if="addressKind === 'cvz' && meterDetail.house_number">
        <span class="tw-text-gray-500">Huisnummer</span>
        <span class="tw-font-medium">{{ meterDetail.house_number }}</span>
      </template>
      <!-- first and last data of the meter, whatever reads it (ClickHouse meter_span) -->
      <template v-if="device?.first_data_at">
        <span class="tw-text-gray-500">Eerste productie</span>
        <span class="tw-font-medium" :title="formatDateTimeNL(device.first_data_at)">
          {{ formatDate(device.first_data_at) }}
          <span class="tw-text-gray-400 tw-font-normal">({{ formatRelativeNL(device.first_data_at) }})</span>
        </span>
      </template>
      <template v-if="device && !dataSpanKnown">
        <span class="tw-text-gray-500">Laatste productie</span>
        <span class="tw-text-gray-400">…</span>
      </template>
      <template v-else-if="device">
        <span class="tw-text-gray-500">Laatste productie</span>
        <span
          class="tw-font-medium"
          :class="productionStale ? 'tw-text-red-600' : ''"
          :title="device.last_data_at ? formatDateTimeNL(device.last_data_at) : 'Nog geen productiedata ontvangen'"
        >
          <template v-if="device.last_data_at">
            {{ formatRelativeNL(device.last_data_at) }}
          </template>
          <template v-else>Nog geen data</template>
        </span>
      </template>
      <template v-if="meterDetail.date_activated">
        <span class="tw-text-gray-500">Activering</span>
        <span class="tw-font-medium">{{ formatDate(meterDetail.date_activated) }}</span>
      </template>
      <template v-if="meterDetail.last_successful_communication">
        <span class="tw-text-gray-500">Laatste comm.</span>
        <span class="tw-font-medium tw-flex tw-items-center tw-gap-1" :class="commStale ? 'tw-text-red-600' : ''">
          {{ formatDate(meterDetail.last_successful_communication) }}
          <span v-if="commStale" class="tw-text-xs tw-font-normal">({{ commStale }}d geleden)</span>
        </span>
      </template>
      <template v-if="meterDetail.last_successful_reading">
        <span class="tw-text-gray-500">Laatste uitlezing</span>
        <span class="tw-font-medium tw-flex tw-items-center tw-gap-1" :class="readingMismatch ? 'tw-text-red-600' : ''">
          {{ formatDate(meterDetail.last_successful_reading) }}
          <div v-if="readingMismatch" class="tw-relative">
            <button
              @click="showMismatchInfo = !showMismatchInfo"
              class="tw-w-4 tw-h-4 tw-rounded-full tw-bg-red-100 tw-text-red-600 tw-text-xs tw-font-bold tw-flex tw-items-center tw-justify-center hover:tw-bg-red-200 tw-transition-colors tw-flex-shrink-0"
            >!</button>
            <div
              v-if="showMismatchInfo"
              class="tw-absolute tw-left-0 tw-top-6 tw-z-50 tw-bg-white tw-border tw-border-gray-200 tw-rounded-lg tw-shadow-lg tw-p-3 tw-text-xs tw-text-gray-700 tw-w-64"
            >
              <div class="tw-font-semibold tw-text-red-600 tw-mb-1">Data loopt {{ readingMismatch }} dagen achter</div>
              <p>De meter communiceert succesvol, maar de ontvangen data bevat verouderde timestamps.</p>
            </div>
          </div>
        </span>
      </template>
    </div>

    <!-- Status error detail -->
    <div v-if="meterDetail.status && String(meterDetail.status).split(':').length > 1" class="tw-text-xs tw-text-red-600 tw-bg-red-50 tw-rounded tw-p-2 tw-mt-2">
      {{ meterDetail.status }}
    </div>
  </div>
</template>

<script setup>
import { ref, computed } from 'vue'
import { formatDateNL as formatDate, formatDateTimeNL, formatRelativeNL } from './formatters'

// What we know of a meter whatever reads it. What only the polling server, Cast4all or SolarEdge knows
// is in the vendor column next to it (MeterPanel).
const props = defineProps({
  serialNumber: { type: String, required: true },
  meterDetail: { type: Object, required: true },
  isLoading: { type: Boolean, default: false },
  // this meter's entry of the address view: first_data_at, last_data_at
  device: { type: Object, default: null },
  addressKind: { type: String, default: 'regular' },
})

const showMismatchInfo = ref(false)

const aliveClass = computed(() => {
  if (props.meterDetail.alive === true) return 'tw-bg-green-500'
  if (props.meterDetail.alive === false) return 'tw-bg-red-500'
  return 'tw-bg-gray-300'
})

const statusBadgeClass = computed(() => {
  const status = String(props.meterDetail.status || '')
  if (status.includes('failed')) return 'tw-bg-red-100 tw-text-red-700'
  if (status === 'Registered') return 'tw-bg-green-100 tw-text-green-700'
  return 'tw-bg-gray-100 tw-text-gray-600'
})

const statusLabel = computed(() => String(props.meterDetail.status || '').split(':')[0])

// the address view is still loading (or failed): the meter's data span is not known yet
const dataSpanKnown = computed(() => props.device?.dataSpanKnown !== false)

// red when the newest data is more than a day old: every source delivers at least daily
const productionStale = computed(() => {
  const last = props.device?.last_data_at
  return !last || Date.now() - new Date(last).getTime() > 24 * 3600 * 1000
})

// ─── Comm / reading freshness checks ────────────────────────────────────────

const commStale = computed(() => {
  const comm = props.meterDetail?.last_successful_communication
  if (!comm) return null
  const diffDays = Math.round((new Date() - new Date(comm)) / 86400000)
  return diffDays > 2 ? diffDays : null
})

const readingMismatch = computed(() => {
  const comm = props.meterDetail?.last_successful_communication
  const reading = props.meterDetail?.last_successful_reading
  if (!comm || !reading) return null
  const diffDays = Math.round((new Date(comm) - new Date(reading)) / 86400000)
  return diffDays > 2 ? diffDays : null
})
</script>
